import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AberrationTabComponent } from '../aberration-tab/aberration-tab.component';
import { TranslateService } from '@ngx-translate/core';
import { ScrollToService } from '@nicky-lenaers/ngx-scroll-to';
import { takeUntil, distinctUntilChanged, filter, tap, map } from 'rxjs/operators';

interface CustomCategorySource {
  name: string;
  type: string;
  category?: string;
}

interface CustomCategory {
  name: string;
  sources: CustomCategorySource[];
}

@Component({
  selector: 'aberration-info-tab',
  templateUrl: './aberration-info-tab.component.html',
  styleUrls: ['./aberration-info-tab.component.scss']
})
export class AberrationInfoTabComponent extends AberrationTabComponent implements OnInit {

  @ViewChild('content_pane', {static: false}) contentPane: ElementRef;
  annotationsCategories: string[];
  therapyMatchCategories: string[];
  clinicalTrialCategories: string[];
  aberrationContent: any;
  aberrationExtras: { [key: string]: any};
  aberrationExtrasFiltered: { [key: string]: any};
  therapyMatches: { [key: string]: any};
  clinicalTrials: { [key: string]: any};
  currantSection: string;
  clickEvent: boolean;

  constructor(
    private translate: TranslateService,
    private scrollToService: ScrollToService
  ) {
    super();
  }

   ngOnInit() {
    this.aberration
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        filter(aberration => !!aberration),
        tap(aberration => {
          this.aberrationContent = aberration;
          this.aberrationExtras = Object.assign({ }, aberration.extra);

          this.therapyMatches = aberration.therapyMatches;
          this.therapyMatchCategories = Object.keys(aberration.therapyMatches);

          this.clinicalTrials = aberration.clinicalTrials;
          this.clinicalTrialCategories = Object.keys(aberration.clinicalTrials);
        }),
        map(aberration => Object.keys(aberration.extra).map(category => {
          return {
            translationCategory: category,
            preTranslationCategory: aberration.extra[category].preTranslationCategory
          };
        }))
      )
      .subscribe(categories => {
        this.annotationsCategories = categories.filter(category => {
          if (!!this.testTableData.displayAnnotations) {
            return !!this.testTableData.displayAnnotations[category.preTranslationCategory];
          }

          return true;
        }).map(category => category.translationCategory);

        this.aberrationExtrasFiltered = this.filterDisplayedAnnotations(this.aberrationExtras);

        if (this.configuration && this.configuration.customCategories) {
          this.annotationsCategories.push(this.configuration.customCategories.map(category =>
            this.translate.instant('annotation.' + category.name, category.name))
          );
          this.configuration.customCategories.forEach(cat => this.generateCustomCategories(cat));
        }

        this.currantSection = 'category_' + (this.therapyMatchCategories[0]
          ? this.therapyMatchCategories[0] : this.annotationsCategories[0]);
      });

  }

  scrollTo(id: string) {
    this.clickEvent = true;
    this.scrollToService
      .scrollTo({ target: id })
      .subscribe(section => this.currantSection = id)
    ;
  }

  onScrollLocationChanged(event) {
    if (!this.clickEvent) {
      this.currantSection = event.id;
    }

    this.clickEvent = false;
  }

  aberrationInfoTabContentUpdated($event) {
    this.contentUpdated.emit($event);
  }

  private filterDisplayedAnnotations(annotations) {
    const filteredAnnotations = { };
    Object.keys(annotations)
      .map(annotationCategory => annotations[annotationCategory])
      .forEach(annotationGroup => {
        const annotationCategory = annotationGroup.preTranslationCategory;
        filteredAnnotations[annotationGroup.translatedCategory] = { annotations: []};

        if (this.testTableData.displayAnnotations && this.testTableData.displayAnnotations[annotationCategory]) {
          const categoryData = this.testTableData.displayAnnotations[annotationCategory];
          const postFilter = annotationGroup.annotations.filter(annotation =>
            annotation.annotationSource ?
              this.checkAnnotationInConfig(categoryData, annotation) && this.checkAnnotationSourceInConfig(categoryData, annotation.annotationSource)
              : this.checkAnnotationInConfig(categoryData, annotation)
          );
          filteredAnnotations[annotationGroup.translatedCategory].annotations.push(...postFilter);
        }
        else {
          filteredAnnotations[annotationGroup.translatedCategory] = annotationGroup; // { annotations: annotationGroup.annotations};
        }

        filteredAnnotations[annotationGroup.translatedCategory].annotationResultList  = annotationGroup.annotationResultList;
        filteredAnnotations[annotationGroup.translatedCategory].preTranslationCategory = annotationGroup.preTranslationCategory;
      });
    return filteredAnnotations;
  }

  generateCustomCategories(category: CustomCategory) {
    const translateCategory = this.translate.instant('annotation.' + category.name, category.name);
    this.aberrationExtras[translateCategory] = { annotations: [] };
    category.sources
      .forEach(annSrc => {
        let value;

        switch (annSrc.type) {
          case 'Aberration':
            value = this.aberrationContent[annSrc.name];
            break;
          case 'Annotation':
            const translateAnnCategory = this.translate.instant('annotation.' + annSrc.category, annSrc.category);
            value = this.aberrationExtras[translateAnnCategory].annotations
              .filter(ann => ann.name.indexOf(annSrc.name) > -1)[0].value;
            break;
        }

        this.aberrationExtras[translateCategory].annotations.push({
          name: annSrc.name,
          value: value
        });
      });
  }

  checkAnnotationSourceInConfig(categoryData, annotationSource) {
    if (categoryData.allowedSources) {
      const isAllowedSourcesAll = categoryData.allowedSources.length > 0 && categoryData.allowedSources[0] === 'ALL';

      if (isAllowedSourcesAll) {
        return true;
      }

      return categoryData.allowedSources.some(source => {
        return source === annotationSource;
      });
    }

    return true;
  }

  checkAnnotationInConfig(categoryData, annotation) {
    if (categoryData.allowedAnnotations) {
      const isAllowedAnnotationsAll = categoryData.allowedAnnotations.length > 0 && categoryData.allowedAnnotations[0] === 'ALL';

      if (isAllowedAnnotationsAll) {
        return true;
      }

      return categoryData.allowedAnnotations.some(allowedAnn => {
        return allowedAnn === annotation.name;
      });
    }
    else { // support old format
      if (Array.isArray(categoryData)) {
        return categoryData.indexOf(annotation.name) != -1;
      }
    }
  }
}
